import {
  addMinutes,
  differenceInMinutes,
  eachDayOfInterval,
  endOfDay,
  format,
  formatISO,
  isMonday,
  isSunday,
  nextSunday,
  parse,
  parseISO,
  previousMonday,
  set,
  startOfDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import {
  dateFormat,
  longDateOnly,
  shortDateOnly,
  timeFormat,
} from '../constants/date-format-constants';
import { translations } from '../constants/translations';

export const getFromDate = (): Date => {
  const monday = isMonday(new Date())
    ? startOfDay(new Date())
    : startOfDay(previousMonday(new Date()));
  return monday;
};

export const getToDate = (): Date => {
  const sunday = isSunday(new Date())
    ? endOfDay(new Date())
    : endOfDay(nextSunday(new Date()));
  return sunday;
};

export const getAllWeekdays = (from: Date, to: Date): Date[] => {
  return eachDayOfInterval({ start: from, end: to });
};

export const getWeekdayNameShortcut = (
  date: Date,
  translationData: typeof translations
) => {
  switch (date.getDay()) {
    case 0:
      return translationData.weekdaySu;
    case 1:
      return translationData.weekdayMo;
    case 2:
      return translationData.weekdayTu;
    case 3:
      return translationData.weekdayWe;
    case 4:
      return translationData.weekdayTh;
    case 5:
      return translationData.weekdayFr;
    case 6:
      return translationData.weekdaySa;
    default:
      console.error('Not a valid date. Day not found');
      return 'Nd';
  }
};

export const formatDateString = (date: Date) => {
  return format(date, dateFormat, { locale: de });
};

export const formatTimeString = (date: Date) => {
  return format(date, timeFormat, { locale: de });
};

export const formatShortDateOnlyString = (date: Date) => {
  return format(date, shortDateOnly, { locale: de });
};

export const formatLongDateOnlyString = (date: Date) => {
  return format(date, longDateOnly, { locale: de });
};

export const formatIsoDateString = (date: Date) => {
  return formatISO(date);
};

export const formatSeparateDateAndTimeString = (date: Date, time: Date) => {
  const newDate = set(time, {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
  });
  return formatDateString(newDate);
};

export const parseDateString = (dateString: string | undefined) => {
  if (!dateString) {
    console.error('no date provided using current date');
    return new Date();
  }
  return parse(dateString, dateFormat, 0, { locale: de });
};

export const parseTimeString = (timeString: string) => {
  return parse(timeString, timeFormat, 0, { locale: de });
};

export const parseIsoDateString = (dateString: string) => {
  return parseISO(dateString);
};

export const durationInMinutes = (startDate: Date, endDate: Date) => {
  return differenceInMinutes(endDate, startDate);
};

export const getEndDate = (startDateString: string, durationMins: number) => {
  return addMinutes(parseDateString(startDateString), durationMins);
};
